import { Formik } from 'formik';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';

import Loading from '@/components/loading/Loading';
import Step from '@/components/wizard/Step';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import StepFooter from '@/mp/components/Payment/StepFooter';
import { usePaymentContext } from '@/mp/hooks/usePaymentContext';
import ProjectDetailsForm from '@/pages/billing/ProjectDetailsForm/Form';
import { orderV2Actions } from '@/redux/reducers/order.v2';
import {
  useGetJobQuery,
  useUpdateJobMutation
} from '@/redux/rtk';
import { orderV2Selectors } from '@/redux/selectors/order.v2';
import routes from '@/routes/constants';
import text from '@/text';
import { getProjectName } from '@/utilities/project';
import { getNowDate } from '../../../../utilities/date';
import { UploadProvider } from '../../../context/UploadContext';
import useRedirectToMap from '../../../hooks/useRedirectToMap';

type FormValues = {
  message: string | null;
  projectName: string | null;
  customer_software: string | null;
};

const validationSchema = yup.object({
  customer_software: yup
    .string()
    .oneOf([
      'autodesk_revit',
      'autodesk_autocad',
      'graphisoft_archicad',
      'bentley',
      'vectorworks',
      'rhinoceros_rhino',
      'sketchup_scan_plugin',
    ])
    .required(text('required')),
  message: yup.string().nullable().optional(),
  projectName: yup
    .string()
    .required(text('pleaseSelectOrCreateProject'))
    .typeError(text('pleaseTypeProjectName')),
});

interface ProjectDetailsStepProps {
  nextLabel?: string;
}

const ProjectDetailsStep: React.FC<ProjectDetailsStepProps> = ({
  nextLabel,
}) => {
  const { jobId, asMember } = usePaymentContext();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [updateJob] = useUpdateJobMutation();
  const { data: currentJob } = useGetJobQuery(jobId, {
    skip: !jobId,
  });

  const nonRfqCharges = useAppSelector((state) =>
    orderV2Selectors.getNonRfqCharges(state, jobId, asMember)
  );

  const isRfqOrderOnly = nonRfqCharges?.length === 0;

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      // save customer software and message on next page click
      await updateJob({
        id: jobId,
        data: {
          customer_software: values.customer_software,
          message: values.message,
          updatedAt: getNowDate(),
        },
      }).unwrap();

      dispatch(orderV2Actions.setCustomerSoftware(values.customer_software));
      dispatch(orderV2Actions.setComments(values.message));

      if (isRfqOrderOnly && currentJob) {
        history.push(routes.fileManager.viewProject(currentJob.project.id));
      }
    },
    [dispatch, jobId, updateJob]
  );

  const { redirectToMap } = useRedirectToMap();

  // {/* we can only show the form once the job is loaded so that the initial values are correct */}
  return (
    currentJob ? (
      <UploadProvider jobId={jobId} projectId={currentJob.project.id} autoUpload={true}>
        <Formik
          validateOnMount
          validateOnChange
          validateOnBlur
          initialValues={{
            projectId: currentJob?.project.id ?? null,
            projectName: getProjectName(currentJob?.project),
            customer_software: currentJob?.customer_software ?? null,
            message: currentJob?.message ?? null,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isValid }) => {
            return (
              <>
                {/* @ts-expect-error: wrong types */}
                <Step>
                  <ProjectDetailsForm
                    disableProjectSelect={!!currentJob.map2d_id}
                  />
                </Step>
                <StepFooter
                  submit
                  nextButtonProps={{
                    type: 'submit',
                    label: nextLabel ?? text('continue'),
                    disabled: !isValid,
                  }}
                  onCancel={redirectToMap}
                />
              </>
            );
          }}
        </Formik>
      </UploadProvider>
    ) : <Loading />
  );
};

export default ProjectDetailsStep;
